/**
 * Adversarial input corpus for the regex performance probe (VE-24948 / U5,
 * ADR-001 as amended).
 *
 * WHY THIS EXISTS. Three successive attempts to decide a pattern's safety by
 * INSPECTING it were each bypassed by measurement (review gates #1-#3: the last
 * accepted `(\w)*` x10 at 133 s on a 40-character input while rejecting
 * `(?:ab)+`). A static screen has to prove a safety property about a language it
 * does not fully parse, so every gap in the parse becomes an unsafe default in
 * one direction and a false rejection in the other. The gate is now a
 * MEASUREMENT: run the pattern and time it. This module builds what to run it
 * against.
 *
 * PAGE SCALE IS THE POINT. Every calibration behind the old rules topped out at
 * 120 characters, and that single mistake is why all three under-estimated: for
 * polynomial backtracking, input length dominates pattern shape. `[0-Z]*\W*` x4
 * on 60 characters (419 s) is worse than x5 on 40 (245 s). Real page text is
 * thousands of characters, so `REGEX_PROBE_INPUT_LENGTH` is thousands and must
 * stay that way.
 *
 * WHY THE FILLERS COME FROM THE PATTERN. Catastrophic backtracking needs an
 * input the pattern can ALMOST consume: `\w*\w*z` is instant on `'!!!!'` and
 * quadratic on `'aaaa'`. A fixed corpus therefore misses whatever it happens
 * not to spell, so the seeds are derived from the pattern's own literals and
 * character classes, and each candidate string ends in a sentinel the seeds do
 * not contain, so the match runs the length of the input and then FAILS — the
 * worst case, not the lucky one.
 *
 * WHY EVERY INPUT CARRIES A SYNTHESIZED LEAD-IN. Seeds alone were not enough,
 * and the gap was a bypass, not a refinement (review gate #4). A pattern whose
 * ambiguity sits BEHIND a literal never reaches that ambiguity on a corpus of
 * repeated single characters: `x(a+)+z` finds no `x` in a string of `a`s and no
 * `a` in a string of `x`s, so it matched nothing at every position, measured
 * 0 ms and SAVED — then cost 17,045 ms on one 33-character line of real page
 * text. `Case-No-(\d+\s*)+:` is the same shape and is what an ordinary records
 * clerk would type. So each input is now prefixed by `synthesizeLeadIn`: a
 * shortest string satisfying the pattern's leading fixed tokens, which hands the
 * engine a way in to the part that blows up.
 *
 * The lead-in is a prefix and never a repeating unit, and that is deliberate.
 * Interleaving the pattern's literals through the body would let a TRAILING
 * literal be satisfied mid-string — turning the run into a fast SUCCESS and
 * measuring the lucky case, which is exactly what `SENTINEL` exists to prevent.
 * One lead-in at position 0, then seed fill, then the sentinel: strictly more
 * adversarial than the string it replaces, at the same length and the same cost.
 *
 * PURE ON PURPOSE. No worker, no timing, no `RegExp` execution here — so the
 * corpus is assertable in a plain unit test, and the thing that runs it can be
 * a disposable worker without dragging this logic across the boundary.
 */

import { REGEX_PROBE_INPUT_LENGTH, REGEX_PROBE_MAX_INPUTS } from './limits';

/**
 * Characters the corpus always tries, whatever the pattern says.
 *
 * Not redundant with the derived seeds: a pattern built entirely from negated
 * classes (`[^z]*[^y]*q`) names no character it can match, so derivation alone
 * would produce nothing useful. `':'` is here because a bucket-spanning range
 * (`[0-Z]`) matches punctuation that neither endpoint suggests — the shape that
 * bypassed the previous screen at 245 s.
 *
 * `'\n'` is load-bearing and must not be dropped. `.` does not match a newline,
 * so on newline-free text a dot-heavy pattern like `\d*.*\d*.*\d*.*\d*` simply
 * succeeds at position 0 and measures at 0 ms — while the same pattern on real
 * page text, which has newlines, cannot finish. A newline is the only character
 * that forces those patterns to FAIL, and failure is where backtracking bites.
 */
const ALWAYS_SEEDS: readonly string[] = ['a', '0', ' ', ':', '_', '\n'];

/**
 * A character no seed set contains, so a pattern ending in a literal is forced
 * to consume the whole input and then fail. `U+0001` is not producible by any
 * seed above and is not regex-special.
 */
const SENTINEL = '\u0001';

/**
 * Decodes `\xNN`, `\uNNNN`, `\u{...}` and `\cX` at `i` (which points at the
 * character after the backslash) to the character it denotes, plus the length
 * consumed.
 *
 * Required, not a refinement. `\x41*` x10 is `A*` x10 — a quadratic pattern —
 * but seeding from the escape's LETTERS yields `x`, `4`, `1`, so every corpus
 * string would be free of `A`, every `A*` would match empty, and the probe
 * would clock it at 1 ms. A corpus that cannot spell what the pattern matches
 * measures the wrong thing.
 */
const decodeNumericEscape = (
  pattern: string,
  i: number
): { char: string; length: number } | null => {
  const kind = pattern[i];
  if (kind === 'x') {
    const hex = pattern.slice(i + 1, i + 3);
    return /^[0-9a-fA-F]{2}$/.test(hex)
      ? { char: String.fromCharCode(Number.parseInt(hex, 16)), length: 3 }
      : null;
  }
  if (kind === 'u') {
    if (pattern[i + 1] === '{') {
      const close = pattern.indexOf('}', i + 2);
      const hex = close === -1 ? '' : pattern.slice(i + 2, close);
      return /^[0-9a-fA-F]{1,6}$/.test(hex)
        ? {
            char: String.fromCodePoint(Number.parseInt(hex, 16)),
            length: close - i + 1,
          }
        : null;
    }
    const hex = pattern.slice(i + 1, i + 5);
    return /^[0-9a-fA-F]{4}$/.test(hex)
      ? { char: String.fromCharCode(Number.parseInt(hex, 16)), length: 5 }
      : null;
  }
  if (kind === 'c') {
    const letter = pattern[i + 1];
    return letter !== undefined && /^[a-zA-Z]$/.test(letter)
      ? {
          char: String.fromCharCode(letter.toUpperCase().charCodeAt(0) - 64),
          length: 2,
        }
      : null;
  }
  return null;
};

/** Representative members of each class escape, for seeding only. */
const ESCAPE_SEEDS: Readonly<Record<string, readonly string[]>> = {
  d: ['0'],
  D: ['a', ' '],
  w: ['a', '0', '_'],
  W: [' ', ':'],
  s: [' ', '\t'],
  S: ['a', '0'],
  n: ['\n'],
  r: ['\r'],
  t: ['\t'],
  f: ['\f'],
  v: ['\v'],
};

/**
 * Characters a bracketed class suggests. `source` spans `[` to `]`.
 *
 * A range contributes its endpoints AND a midpoint, because a range that spans
 * a bucket boundary (`0-Z`) matches characters neither endpoint hints at, and
 * the midpoint is the cheapest way to reach them without enumerating.
 */
const seedsOfBracket = (source: string): readonly string[] => {
  const seeds: string[] = [];
  const negated = source[1] === '^';
  let j = negated ? 2 : 1;
  const end = source.length - 1;
  while (j < end) {
    const c = source[j];
    if (c === undefined) {
      break;
    }
    if (c === '\\') {
      const esc = source[j + 1];
      const decoded = decodeNumericEscape(source, j + 1);
      if (decoded) {
        seeds.push(decoded.char);
        j += 1 + decoded.length;
        continue;
      }
      if (esc !== undefined) {
        seeds.push(...(ESCAPE_SEEDS[esc] ?? [esc]));
      }
      j += 2;
      continue;
    }
    const next = source[j + 1];
    const after = source[j + 2];
    if (next === '-' && after !== undefined && after !== ']') {
      const lo = c.charCodeAt(0);
      const hi = after.charCodeAt(0);
      seeds.push(c, after);
      if (hi > lo + 1) {
        seeds.push(String.fromCharCode(Math.floor((lo + hi) / 2)));
      }
      j += 3;
      continue;
    }
    seeds.push(c);
    j += 1;
  }
  // A negated class matches everything its body does NOT, so its own members
  // are the wrong fillers — `ALWAYS_SEEDS` covers that case instead.
  return negated ? [] : seeds;
};

/** Regex syntax that is structure, not input. */
const META: ReadonlySet<string> = new Set([
  '(',
  ')',
  '[',
  ']',
  '{',
  '}',
  '|',
  '*',
  '+',
  '?',
  '.',
  '^',
  '$',
  '/',
]);

/**
 * Characters worth filling an input with, derived from the pattern itself.
 *
 * Deliberately over-inclusive: a seed that turns out to be irrelevant costs one
 * extra corpus string, while a missing seed costs a bypass.
 */
export const seedCharacters = (pattern: string): readonly string[] => {
  const seeds: string[] = [...ALWAYS_SEEDS];
  let i = 0;
  while (i < pattern.length) {
    const c = pattern[i];
    if (c === undefined) {
      break;
    }
    if (c === '\\') {
      const esc = pattern[i + 1];
      const decoded = decodeNumericEscape(pattern, i + 1);
      if (decoded) {
        seeds.push(decoded.char);
        i += 1 + decoded.length;
        continue;
      }
      if (esc !== undefined) {
        seeds.push(...(ESCAPE_SEEDS[esc] ?? [esc]));
      }
      i += 2;
      continue;
    }
    if (c === '[') {
      const close = pattern.indexOf(']', i + 1);
      if (close === -1) {
        i += 1;
        continue;
      }
      seeds.push(...seedsOfBracket(pattern.slice(i, close + 1)));
      i = close + 1;
      continue;
    }
    if (c === '.') {
      // `.` matches every seed already present, so it adds nothing of its own.
      i += 1;
      continue;
    }
    if (!META.has(c)) {
      seeds.push(c);
    }
    i += 1;
  }
  // De-duplicate keeping first-seen order, so the always-seeds lead and a
  // truncated corpus still covers the generic battery.
  return [...new Set(seeds)];
};

/**
 * Upper bound on a synthesized lead-in, in characters.
 *
 * The lead-in only has to get the engine PAST the pattern's fixed head and into
 * the ambiguous tail; every character it spends is one fewer character of
 * adversarial fill behind it. 64 covers the realistic heads (`Case-No-`,
 * `[A-Z]{2}-`, an SSN prefix) while leaving the body essentially page-scale.
 */
const LEAD_IN_MAX_LENGTH = 64;

/** Cap on how many times one bounded-repetition token expands into the lead-in. */
const LEAD_IN_MAX_REPEAT = 8;

/**
 * Escapes that match no character, so they contribute nothing to a lead-in.
 *
 * Emitting a literal `b` for `\b` would be worse than emitting nothing: it moves
 * the lead-in off the position the anchor actually describes.
 */
const ZERO_WIDTH_ESCAPES: ReadonlySet<string> = new Set(['b', 'B']);

/** Structure that ends a lead-in: past here the pattern stops being fixed. */
const LEAD_IN_STOP: ReadonlySet<string> = new Set(['(', ')', '|', '$']);

/** One character a bracketed class matches, for lead-in synthesis. */
const representativeOfBracket = (source: string): string | null => {
  const [first] = seedsOfBracket(source);
  if (first !== undefined) {
    return first;
  }
  // Negated (or empty): `seedsOfBracket` deliberately yields nothing, since a
  // negated class matches everything its body does NOT. Any always-seed the
  // body does not list is therefore a member.
  const negated = source[1] === '^';
  const body = source.slice(negated ? 2 : 1, -1);
  return ALWAYS_SEEDS.find((seed) => !body.includes(seed)) ?? null;
};

/**
 * Reads the quantifier at `i`, returning how many times the token before it
 * should be repeated in a SHORTEST satisfying string, and how much to consume.
 *
 * Shortest, not longest: `?` and `*` contribute nothing, because the lead-in's
 * job is to reach the ambiguous tail, not to pad it. A `{n,m}` contributes `n`
 * (capped) because fewer would not match at all.
 */
const quantifierAt = (
  pattern: string,
  i: number
): { readonly count: number; readonly length: number } => {
  const c = pattern[i];
  const lazy = (at: number): number => (pattern[at] === '?' ? 1 : 0);
  if (c === '?' || c === '*') {
    return { count: 0, length: 1 + lazy(i + 1) };
  }
  if (c === '+') {
    return { count: 1, length: 1 + lazy(i + 1) };
  }
  if (c === '{') {
    const close = pattern.indexOf('}', i + 1);
    if (close !== -1) {
      const match = /^(\d+)(?:,\d*)?$/.exec(pattern.slice(i + 1, close));
      const low = match?.[1];
      if (low !== undefined) {
        return {
          count: Math.min(
            Math.max(Number.parseInt(low, 10), 1),
            LEAD_IN_MAX_REPEAT
          ),
          length: close - i + 1 + lazy(close + 1),
        };
      }
    }
  }
  return { count: 1, length: 0 };
};

/**
 * A shortest string satisfying the pattern's leading FIXED tokens.
 *
 * Walks literals, escapes, classes and `.` — each with its quantifier — and
 * stops at the first group, alternation or end anchor, because that is where
 * the pattern stops being a fixed head and starts being the part whose cost we
 * are trying to measure. Returns `''` for a pattern that begins with a group
 * (the canonical email pattern, `(a+)+z`), which leaves the corpus exactly as
 * it was for those.
 *
 * Deliberately approximate, in the safe direction. A lead-in that does not in
 * fact match costs one wasted prefix on an input that still carries its full
 * seed fill; the corpus is no worse than before. A lead-in that DOES match is
 * the difference between measuring the pattern and measuring nothing.
 */
export const synthesizeLeadIn = (pattern: string): string => {
  let out = '';
  // A leading `^` is not part of the lead-in, but it does not end one either:
  // it pins the match to position 0, which is exactly where the lead-in sits.
  let i = pattern.startsWith('^') ? 1 : 0;

  while (i < pattern.length && out.length < LEAD_IN_MAX_LENGTH) {
    const c = pattern[i];
    if (c === undefined || LEAD_IN_STOP.has(c)) {
      break;
    }

    let rep: string | null;
    let consumed: number;

    if (c === '\\') {
      const esc = pattern[i + 1];
      if (esc === undefined || ZERO_WIDTH_ESCAPES.has(esc)) {
        i += 2;
        continue;
      }
      const decoded = decodeNumericEscape(pattern, i + 1);
      if (decoded) {
        rep = decoded.char;
        consumed = 1 + decoded.length;
      } else {
        const [seed] = ESCAPE_SEEDS[esc] ?? [esc];
        rep = seed ?? null;
        consumed = 2;
      }
    } else if (c === '[') {
      const close = pattern.indexOf(']', i + 1);
      if (close === -1) {
        break;
      }
      rep = representativeOfBracket(pattern.slice(i, close + 1));
      consumed = close - i + 1;
    } else if (c === '.') {
      rep = 'a';
      consumed = 1;
    } else if (META.has(c)) {
      // A bare quantifier or `^` mid-pattern: nothing fixed left to synthesize.
      break;
    } else {
      rep = c;
      consumed = 1;
    }

    if (rep === null) {
      break;
    }

    const quantifier = quantifierAt(pattern, i + consumed);
    out += rep.repeat(quantifier.count);
    i += consumed + quantifier.length;
  }

  return out.slice(0, LEAD_IN_MAX_LENGTH);
};

/**
 * The inputs to time this pattern against.
 *
 * One string per seed (that seed repeated to page length), plus one that cycles
 * every seed — which is what catches a pattern whose blowup needs variety
 * rather than a single repeated character. Each is PREFIXED by the pattern's
 * synthesized lead-in, so a pattern whose ambiguity sits behind a fixed head is
 * actually reached, and each ends in `SENTINEL` so a pattern anchored on a
 * trailing literal must consume everything and then fail.
 *
 * Capped at `REGEX_PROBE_MAX_INPUTS`: the probe is a save-time gate, so its own
 * cost is a product decision rather than an afterthought.
 *
 * WHAT TRUNCATION DROPS IS PART OF THE GATE, and neither list may win it.
 * Truncation used to keep the always-seeds and drop the derived ones, which
 * lost `abcd(q+)+z` — `q` is the only character its blowup can eat, and it fell
 * off the end behind six generic seeds, measuring 0 ms even WITH a lead-in.
 * Simply reversing that lost `Case-No-(\d+\s*)+:` instead, because the digits
 * ITS blowup eats come from the generic `'0'`. The two lists are therefore
 * INTERLEAVED, so a pattern that needs one of each keeps one of each.
 *
 * `'\n'` is pinned ahead of both: it is load-bearing for an unrelated reason
 * (see `ALWAYS_SEEDS`) and is last in the generic list, so interleaving alone
 * would drop it exactly when the corpus is under pressure.
 */
export const buildProbeCorpus = (pattern: string): readonly string[] => {
  const seeds = seedCharacters(pattern);
  // `'\n'`, then derived and generic seeds interleaved — see above.
  const isGeneric: ReadonlySet<string> = new Set(ALWAYS_SEEDS);
  const derived = seeds.filter((seed) => !isGeneric.has(seed));
  const generic = seeds.filter((seed) => isGeneric.has(seed));
  const interleaved: string[] = [];
  for (let i = 0; i < Math.max(derived.length, generic.length); i += 1) {
    const fromDerived = derived[i];
    const fromGeneric = generic[i];
    if (fromDerived !== undefined) {
      interleaved.push(fromDerived);
    }
    if (fromGeneric !== undefined) {
      interleaved.push(fromGeneric);
    }
  }
  const prioritized = [...new Set(['\n', ...interleaved])];
  const body = Math.max(1, REGEX_PROBE_INPUT_LENGTH - SENTINEL.length);
  // One lead-in at position 0 — never repeated through the body. See the header:
  // interleaving it would let a trailing literal match and measure the lucky case.
  const leadIn = synthesizeLeadIn(pattern).slice(0, Math.max(0, body - 1));
  const tail = body - leadIn.length;
  const fill = (source: string): string =>
    leadIn +
    source.repeat(Math.ceil(tail / source.length)).slice(0, tail) +
    SENTINEL;
  const inputs = prioritized
    .slice(0, Math.max(1, REGEX_PROBE_MAX_INPUTS - 1))
    .map(fill);
  const cycled = seeds.join('');
  if (cycled.length > 0) {
    inputs.push(fill(cycled));
  }
  return inputs;
};
